import { DocumentSelectOption, Dokument, HaftpflichtSchaden } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { MATCH_COMPARE_WITH_HIDDEN } from '../../matchers/compare-with-visible.matcher';
import { TYPE_DOKUMENTEN_ANGABE } from '../dokumenten-angabe/dokumenten-angabe.model';
import { TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { HaftpflichtAdditional, HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { GeschaedigtesTierVorhanden } from './haftpflicht-tierschaden.model';

const DOK_TYP_TIERARZTRECHNUNG: DocumentSelectOption = {
  label:                      'Tierarztrechnung',
  value:                      'TIER_ARR',
  zahlungsempfaengerRequired: true
};

export function HAFTPFLICHT_TIERSCHADEN_TEMPLATE(
  value: HaftpflichtSchaden,
  modelValue,
  {
    docTypBelege,
    geschPersList,
    kundeList,
    updateClaim,
    paperProcessOverride
  }: HaftpflichtAdditional = {},
  isEditEvent = false
): AnyFormControlModel[] {
  const {
    id,
    vorhanden,
    geschaedigtePerson,
    belege = []
  } = value || {};
  const {
    vorhanden: tierVorhanden,
    bezeichnung,
    beschreibung,
    alter
  } =
  (geschaedigtePerson?.geschaedigtesTierList && geschaedigtePerson?.geschaedigtesTierList[0]) || {};

  // If belege doesn't include a required Tierarztrechnung, add it.
  const belegeValue: Dokument[] = belege.some(beleg => beleg.required && beleg.typ?.value === DOK_TYP_TIERARZTRECHNUNG.value)
    ? belege
    : [
      {
        typ:         DOK_TYP_TIERARZTRECHNUNG,
        bezeichnung: DOK_TYP_TIERARZTRECHNUNG.label,
        required:    true,
        vorhanden:   'V'
      },
      ...belege
    ];

  if (!isEditEvent && geschPersList && modelValue.length) {
    // only persons who are not in the local modelValue
    geschPersList = geschPersList.filter(
      geschPers => !modelValue.some(val => val.geschaedigtePerson?.correlationId === geschPers.correlationId)
    );
  }

  return [
    {
      id:         'geschaedigtePerson',
      type:       TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON,
      value:      {
        id,
        vorhanden,
        geschaedigtePerson
      },
      additional: {
        geschPersList,
        updateClaim,
        paperProcessOverride,
        detailsNachbringen: tierVorhanden === GeschaedigtesTierVorhanden.Nachgebracht
      }
    },
    {
      id:        'aufstellung_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Tier',
      hidden:    true,
      layout:    {
        element: {
          host: 'gsm-section-headline-3'
        }
      },
      relations: [
        {
          match: MATCH_COMPARE_WITH_HIDDEN,
          when:  [
            {
              id:          'geschaedigtePerson',
              compareWith: val => !val?.vorhanden || val.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
            }
          ]
        }
      ],
      group:     [
        {
          id:       'aufstellungVorhanden',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:    'Tierdaten erfassen',
          required: true,
          options:  [
            ...(paperProcessOverride ? [
              {
                label: 'Siehe Beilage',
                value: GeschaedigtesTierVorhanden.SieheBeilage
              }
            ] : []),
            {
              label: 'Tierdaten erfassen',
              value: GeschaedigtesTierVorhanden.Erfassen
            },
            {
              label: 'Tierdaten werden nachgebracht',
              value: GeschaedigtesTierVorhanden.Nachgebracht
            }
          ],
          value:    !tierVorhanden && paperProcessOverride ? GeschaedigtesTierVorhanden.SieheBeilage : tierVorhanden
        },
        {
          id:        'geschaedigtesTier_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: GeschaedigtesTierVorhanden.Erfassen
                }
              ]
            }
          ],
          group:     [
            {
              id:            'bezeichnung',
              type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
              label:         'Tiergattung',
              placeholder:   'Bitte geben Sie eine Tiergattung an.',
              required:      true,
              validators:    {
                minLength: 2,
                maxLength: 300
              },
              errorMessages: {
                minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
                required:  '{{ label }} ist ein Pflichtfeld.',
                maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
              },
              value:         bezeichnung
            },
            {
              id:            'beschreibung',
              type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
              label:         'Tierrasse',
              placeholder:   'Bitte geben Sie eine Tierrasse an',
              validators:    {
                minLength: 2,
                maxLength: 300
              },
              errorMessages: {
                minLength: 'Bitte geben Sie mindestens 2 Zeichen ein.',
                maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
              },
              value:         beschreibung
            },
            {
              id:            'alter',
              type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
              label:         'Tieralter',
              placeholder:   'Bitte geben Sie ein Tieralter an.',
              validators:    {
                maxLength: 300
              },
              errorMessages: {
                maxLength: 'Bitte geben Sie maximal 300 Zeichen ein.'
              },
              value:         alter
            }
          ]
        },
        {
          id:        'aufstellungBelege_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Belege zum Tierschaden',
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: GeschaedigtesTierVorhanden.Erfassen
                },
                {
                  id:    'aufstellungVorhanden',
                  value: GeschaedigtesTierVorhanden.SieheBeilage
                }
              ]
            }
          ],
          group:     [
            {
              id:         'aufstellungBelege',
              type:       TYPE_DOKUMENTEN_ANGABE,
              additional: {
                requiredTypes:       [DOK_TYP_TIERARZTRECHNUNG],
                optionalTypes:       docTypBelege,
                kunden:              kundeList,
                immediateValidation: true
              },
              value:      belegeValue
            }
          ]
        }
      ]
    }
  ];
}
